import React, { useState, useEffect, useCallback } from 'react';
import { SquadAnalysisResult, PlayerTrainingNeeds, TrainingGroup, MentorPair } from '../Model';
import { restClient } from '../RestClient';
import { PageLoading } from '../PageLoading';
import { useReferenceData } from '../ReferenceDataContext';
import { ShareDrillModal } from './ShareDrillModal';

interface SquadAnalysisProps {
  onError: (message: string, type?: 'error' | 'warning' | 'info') => void;
}

export function SquadAnalysis({ onError }: SquadAnalysisProps) {
  const { selectedSelectionId, referenceData } = useReferenceData();
  const selectedSelection = referenceData?.selections.find(s => s.id === selectedSelectionId);

  const [analysisResult, setAnalysisResult] = useState<SquadAnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shareDrillTarget, setShareDrillTarget] = useState<{ playerName: string; playerIds: number[]; skillFocus?: string } | null>(null);

  const fetchAnalysis = useCallback(async () => {
    if (selectedSelectionId === null) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await restClient.getSquadAnalysis(selectedSelectionId);
      if (result.IsSuccess) {
        setAnalysisResult(result.Value);
      } else {
        console.error('Error fetching squad analysis:', result.Error);
        onError(`${result.Error.code}: ${result.Error.description}`);
      }
    } catch (error) {
      console.error('Unexpected error fetching squad analysis:', error);
      onError('Unexpected error occurred while loading squad analysis');
    } finally {
      setIsLoading(false);
    }
  }, [selectedSelectionId, onError]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (!selectedSelectionId) {
    return (
      <div className="squad-analysis-page p-6">
        <h1 className="text-xl font-semibold mb-4">Squad Analysis</h1>
        <div className="card">
          <div className="card-body text-center text-muted">
            Please select a squad from the navigation menu.
          </div>
        </div>
      </div>
    );
  }

  if (!analysisResult) {
    return (
      <div className="squad-analysis-page p-6">
        <h1 className="text-xl font-semibold mb-4">Squad Analysis</h1>
        <div className="card">
          <div className="card-body text-center text-muted">
            No analysis data available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="squad-analysis-page p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">Squad Analysis</h1>
          {analysisResult.playerNeeds.some(p => p.isShared) && (
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => setShareDrillTarget({ playerName: 'Team', playerIds: [] })}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                <circle cx="18" cy="5" r="3" />
                <circle cx="6" cy="12" r="3" />
                <circle cx="18" cy="19" r="3" />
                <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
              </svg>
              Share Drill with Team
            </button>
          )}
        </div>
        {selectedSelection && (
          <p className="text-muted mt-2">Analyzing: {selectedSelection.name}</p>
        )}
      </div>

      {/* Individual Training Needs - Grouped by Level */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span>Individual Training Needs</span>
        </h2>
        {analysisResult.playerNeeds.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted">
              No players to analyze.
            </div>
          </div>
        ) : (
          <PlayerNeedsGrouped players={analysisResult.playerNeeds} onShareDrill={setShareDrillTarget} />
        )}
      </section>

      {/* Peer Training Groups */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span>Peer Training Groups</span>
        </h2>
        {analysisResult.peerGroups.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted">
              No peer training groups available. Groups form when multiple players share the same weak skill.
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {analysisResult.peerGroups.map((group, index) => (
              <PeerGroupCard key={`${group.skillFocus}-${index}`} group={group} onShareDrill={setShareDrillTarget} />
            ))}
          </div>
        )}
      </section>

      {/* Mentor Pairings */}
      <section className="mb-8">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <span>Mentor Pairings</span>
        </h2>
        {analysisResult.mentorPairs.length === 0 ? (
          <div className="card">
            <div className="card-body text-center text-muted">
              No mentor pairings available. Pairings form when strong players (score 4-5) can help weaker players (score 1-2).
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-auto-fill-lg gap-4">
            {analysisResult.mentorPairs.map((pair, index) => (
              <MentorPairCard key={`${pair.skillFocus}-${pair.mentor.playerId}-${pair.learner.playerId}-${index}`} pair={pair} onShareDrill={setShareDrillTarget} />
            ))}
          </div>
        )}
      </section>

      {/* Share Drill Modal */}
      {shareDrillTarget && selectedSelectionId && (
        <ShareDrillModal
          playerName={shareDrillTarget.playerName}
          playerIds={shareDrillTarget.playerIds}
          selectionDataId={selectedSelectionId}
          skillFocus={shareDrillTarget.skillFocus}
          onClose={() => setShareDrillTarget(null)}
          onSuccess={(msg) => { onError(msg, 'info'); setShareDrillTarget(null); }}
          onError={onError}
        />
      )}
    </div>
  );
}

type ShareDrillCallback = (target: { playerName: string; playerIds: number[]; skillFocus?: string }) => void;

function PlayerNeedsGrouped({ players, onShareDrill }: { players: PlayerTrainingNeeds[]; onShareDrill: ShareDrillCallback }) {
  const levelOrder = ['Developing', 'Average', 'Good', 'Excellent'];
  const levelConfig: Record<string, { color: string; bgColor: string }> = {
    'Excellent': { color: '#4ade80', bgColor: 'rgba(34, 197, 94, 0.1)' },
    'Good': { color: '#60a5fa', bgColor: 'rgba(59, 130, 246, 0.1)' },
    'Average': { color: '#fbbf24', bgColor: 'rgba(251, 191, 36, 0.1)' },
    'Developing': { color: '#f87171', bgColor: 'rgba(239, 68, 68, 0.1)' }
  };

  const grouped = levelOrder.reduce((acc, level) => {
    const playersInLevel = players.filter(p => p.overallLevel === level);
    if (playersInLevel.length > 0) {
      acc[level] = playersInLevel;
    }
    return acc;
  }, {} as Record<string, PlayerTrainingNeeds[]>);

  return (
    <div className="player-needs-grouped">
      {levelOrder.map(level => {
        const playersInLevel = grouped[level];
        if (!playersInLevel) return null;
        const config = levelConfig[level];

        return (
          <div key={level} className="player-needs-section">
            <div
              className="player-needs-section-header"
              style={{ borderLeftColor: config.color }}
            >
              <span className="player-needs-section-title">{level}</span>
              <span className="player-needs-section-count">{playersInLevel.length} players</span>
            </div>
            <div className="grid grid-cols-auto-fill gap-3">
              {playersInLevel.map(player => (
                <PlayerNeedsCard key={player.playerId} player={player} onShareDrill={onShareDrill} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SharedChip({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      className="shared-status-chip shared-status-chip-clickable"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      aria-label="Share drill with player"
    >
      <svg className="shared-status-chip-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M6 7v10M18 7v10M6 12h12M3 9v6M21 9v6" />
      </svg>
      Training
    </button>
  );
}

function PlayerNeedsCard({ player, onShareDrill }: { player: PlayerTrainingNeeds; onShareDrill: ShareDrillCallback }) {
  return (
    <div className="card card-borderless">
      <div className="card-body-sm">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          {player.playerName}
          {player.isShared && (
            <SharedChip onClick={() => onShareDrill({ playerName: player.playerName, playerIds: [player.playerId] })} />
          )}
        </h3>
        {player.weakSkills.length === 0 ? (
          <p className="text-sm text-muted">No weak skills</p>
        ) : (
          <div className="skill-bars">
            {player.weakSkills.map(skill => (
              <div key={skill.skillName} className="skill-bar-row">
                <div className="skill-bar-label">
                  <span className="skill-bar-name">{skill.skillName}</span>
                  <span className="skill-bar-value">{skill.currentScore}/5</span>
                </div>
                <div className="skill-bar-track">
                  <div
                    className="skill-bar-fill"
                    style={{
                      width: `${(skill.currentScore / 5) * 100}%`,
                      backgroundColor: skill.priority === 'High' ? '#f87171' : '#fbbf24'
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PeerGroupCard({ group, onShareDrill }: { group: TrainingGroup; onShareDrill: ShareDrillCallback }) {
  const sharedMemberIds = group.members.filter(m => m.isShared).map(m => m.playerId);

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex justify-between items-start mb-3">
          <div>
            <h3 className="font-semibold capitalize flex items-center gap-2">
              {group.skillFocus} Training Group
              {sharedMemberIds.length > 0 && (
                <SharedChip onClick={() => onShareDrill({
                  playerName: `${group.skillFocus} Training Group`,
                  playerIds: sharedMemberIds,
                  skillFocus: group.skillFocus
                })} />
              )}
            </h3>
            <p className="text-sm text-muted">{group.description}</p>
          </div>
          <span className="badge badge-primary">{group.members.length} players</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {group.members.map(member => (
            <div key={member.playerId} className="peer-member-chip">
              <span className="member-name">{member.playerName}</span>
              <span className="member-score">{renderStars(member.skillScore)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MentorPairCard({ pair, onShareDrill }: { pair: MentorPair; onShareDrill: ShareDrillCallback }) {
  return (
    <div className="card mentor-pair-card">
      <div className="card-body">
        <div className="mb-3">
          <span className="badge badge-accent capitalize">{pair.skillFocus}</span>
        </div>
        <div className="mentor-pair-content">
          <div className="mentor-card">
            <span className="role-label text-success">Mentor</span>
            <span className="player-name font-semibold flex items-center gap-2">
              {pair.mentor.playerName}
              {pair.mentor.isShared && (
                <SharedChip onClick={() => onShareDrill({ playerName: pair.mentor.playerName, playerIds: [pair.mentor.playerId], skillFocus: pair.skillFocus })} />
              )}
            </span>
            <span className="skill-score">{renderStars(pair.mentor.skillScore)}</span>
          </div>
          <div className="pair-arrow">
            <span>helps</span>
          </div>
          <div className="learner-card">
            <span className="role-label text-info">Learner</span>
            <span className="player-name font-semibold flex items-center gap-2">
              {pair.learner.playerName}
              {pair.learner.isShared && (
                <SharedChip onClick={() => onShareDrill({ playerName: pair.learner.playerName, playerIds: [pair.learner.playerId], skillFocus: pair.skillFocus })} />
              )}
            </span>
            <span className="skill-score">{renderStars(pair.learner.skillScore)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function renderStars(score: number): React.ReactNode {
  const stars = [];
  for (let i = 0; i < 5; i++) {
    stars.push(
      <span key={i} className={i < score ? 'star filled' : 'star empty'}>
        *
      </span>
    );
  }
  return <span className="stars">{stars}</span>;
}
